import { Text as BPText } from "@blueprintjs/core";
import React from "react";
import styled, { css } from "styled-components";

import { Theme } from "../theme/common/theme.js";

export type FontSize = Lowercase<
  "EXTRA_SMALL" | "SMALL" | "DEFAULT" | "LARGE" | "EXTRA_LARGE"
>;
export type FontColor = Lowercase<keyof Theme["fontColor"]>;
export type FontWeight = Lowercase<keyof Theme["fontWeight"]>;
export type FontFamily = Lowercase<keyof Theme["fontFamily"]>;

interface TxtInternalProps {
  $fontSize: FontSize | undefined;
  $fontColor: FontColor | undefined;
  $fontWeight: FontWeight | undefined;
  $fontFamily: FontFamily | undefined;
  $strikethrough: boolean | undefined;
}

export interface TxtProps {
  /** optional: the semantic html element to render as*/
  renderAs?: string;
  fontSize?: FontSize;
  fontColor?: FontColor;
  fontWeight?: FontWeight;
  fontFamily?: FontFamily;
  /**
   * Blueprint uses the `title` prop to display a default tooltip.
   * Use disableTooltip if you want to wrap the tag into a HexTooltip, and do not want duplicate tooltips on the Txt.
   */
  disableTooltip?: boolean;
  ellipsize?: boolean;
  strikethrough?: boolean;

  children?: React.ReactNode;

  // Extra standard props to pass straight through
  className?: string;
  style?: React.CSSProperties;
  title?: string;
  onClick?: React.MouseEventHandler;
}

export const Txt = React.forwardRef<HTMLElement, TxtProps>(function Text(
  {
    children,
    disableTooltip = false,
    ellipsize = false,
    fontColor,
    fontFamily,
    fontSize,
    fontWeight,
    renderAs,
    strikethrough = false,
    title,
    ...rest
  }: TxtProps,
  ref,
) {
  return ellipsize ? (
    // We switch to blueprint's text component only if you want to use the ellipses prop since it's a more expensive component to render
    <StyledBPText
      ref={ref}
      $fontColor={fontColor}
      $fontFamily={fontFamily}
      $fontSize={fontSize}
      $fontWeight={fontWeight}
      $strikethrough={strikethrough}
      ellipsize={true}
      // Cannot ellipses on an inline element like span https://semicolon.dev/tutorial/css/text-overflow-ellipsis-doesnt-work
      // We only use this component when ellipsize = true, so default to div instead of span
      tagName={renderAs ?? "div"}
      // Remove the default HTML tooltip prop if the prop is set to true. Note that the title must equal "", which is treated differently than undefined.
      title={disableTooltip ? "" : title}
      {...rest}
    >
      {children}
    </StyledBPText>
  ) : (
    <TxtInternal
      ref={ref}
      $fontColor={fontColor}
      $fontFamily={fontFamily}
      $fontSize={fontSize}
      $fontWeight={fontWeight}
      $strikethrough={strikethrough}
      as={renderAs ?? "span"}
      title={disableTooltip ? "" : title}
      {...rest}
    >
      {children}
    </TxtInternal>
  );
});

const uppercase = <T extends string>(str: T): Uppercase<T> =>
  str.toUpperCase() as Uppercase<T>;

const SharedCSS = css<TxtInternalProps>`
  font-size: ${({ $fontSize, theme }) =>
    $fontSize ? theme.fontSize[uppercase($fontSize)] : "inherit"};
  color: ${({ $fontColor, theme }) =>
    $fontColor ? theme.fontColor[uppercase($fontColor)] : "inherit"};
  font-weight: ${({ $fontWeight, theme }) =>
    $fontWeight ? theme.fontWeight[uppercase($fontWeight)] : "inherit"};
  /* Line-height is not configurable on this component - always in lockstep with fontSize */
  line-height: ${({ $fontSize, theme }) =>
    $fontSize ? theme.lineHeight[uppercase($fontSize)] : "inherit"};
  text-decoration: ${({ $strikethrough }) =>
    $strikethrough ? "line-through" : "none"};
  font-family: ${({ $fontFamily, theme }) =>
    $fontFamily ? theme.fontFamily[uppercase($fontFamily)] : "inherit"};
`;

const TxtInternal = styled.span<TxtInternalProps>`
  ${SharedCSS}
`;

const StyledBPText = styled(BPText)<TxtInternalProps>`
  ${SharedCSS}
`;
